import React, { useState, useEffect } from 'react';
import Styles from './Step2WebsiteAnalysis.module.scss';
import BSButton from 'Components/Button/BSButton';
import { FiLink, FiRefreshCw, FiCheck, FiAlertCircle, FiFileText, FiSearch, FiDownload, FiX, FiExternalLink } from 'react-icons/fi';
import onBoardingService from 'services/onBoardingService';
import { success as successToast, error as errorToast } from 'helpers/toaster';

const Step2WebsiteAnalysis = ({ data, onUpdate, onNext, onPrevious, onSaveAndExit, onSaveAndContinue, onBrandProfileCreated }) => {
  const [websiteUrl, setWebsiteUrl] = useState(data.websiteUrl || '');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(
    !!data.websiteAnalysisData
  );
  const [analysisData, setAnalysisData] = useState(
    data.websiteAnalysisData || null
  );
  const [canSkip, setCanSkip] = useState(true);

  // Tag input states for editable analysis fields
  const [tagInputs, setTagInputs] = useState({});

  // Blog Discovery States
  const [isDiscoveringBlogs, setIsDiscoveringBlogs] = useState(false);
  const [discoveredBlogs, setDiscoveredBlogs] = useState([]);
  const [selectedBlogs, setSelectedBlogs] = useState([]);
  const [isImportingBlogs, setIsImportingBlogs] = useState(false);
  const [blogImportResults, setBlogImportResults] = useState(null);
  const [showBlogSection, setShowBlogSection] = useState(false);

  // Sync state with props when data changes (e.g., after async load)
  useEffect(() => {
    if (data.websiteUrl) {
      setWebsiteUrl(data.websiteUrl);
    }
    if (data.websiteAnalysisData) {
      setAnalysisData(data.websiteAnalysisData);
      setAnalysisComplete(true);
    }
  }, [data.websiteUrl, data.websiteAnalysisData]);

  // Normalize URL to ensure it has https:// prefix
  const normalizeUrl = (url) => {
    if (!url) return '';
    let normalized = url.trim().toLowerCase();

    // Remove any leading/trailing quotes
    normalized = normalized.replace(/^["']|["']$/g, '');

    // If it doesn't start with http:// or https://, add https://
    if (!normalized.match(/^https?:\/\//)) {
      normalized = `https://${normalized}`;
    }

    // Remove trailing slash
    normalized = normalized.replace(/\/$/, '');

    return normalized;
  };

  const handleAnalyze = async (forceRefresh = false) => {
    if (!websiteUrl.trim()) {
      errorToast('Please enter a website URL');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisComplete(false);

    try {
      // Normalize the URL before sending
      const normalizedUrl = normalizeUrl(websiteUrl);

      // Call website scraping service - now returns analysis immediately!
      // Enable Brand Intelligence auto-population
      // Pass forceRefresh to bypass both DB cache and Jina cache
      const response = await onBoardingService.getAnalyzeBusiness(
        normalizedUrl,
        true, // populate_brand_intelligence flag
        forceRefresh, // force_refresh - bypass cache for re-analyze
        data.brandProfile?.business_id // Pass existing business ID if available
      );

      // Check if analysis was successful
      if (response.data.success && response.data.analysis) {
        setIsAnalyzing(false);
        setAnalysisComplete(true);
        setAnalysisData(response.data.analysis);
        successToast('Website analyzed successfully!');

        // Auto-save analysis data and populate Step 1 fields
        const analysis = response.data.analysis;
        const brandProfile = response.data.brand_profile;

        onUpdate({
          websiteUrl,
          websiteAnalysisData: analysis,
          brandProfile: brandProfile,
          // Populate Step 1 fields from analysis
          name: analysis.brand_name || '',
          offering: analysis.brand_description?.split('.')[0] || '',
          marketPositioning: analysis.core_messaging || '',
          valueProposition: analysis.value_proposition || '',
          uniqueSellingPoint: analysis.value_proposition || '',
        });

        // Notify parent component that brand profile was created
        if (brandProfile && brandProfile.id && onBrandProfileCreated) {
          onBrandProfileCreated(brandProfile.id);
        }
      } else {
        setIsAnalyzing(false);
        errorToast(
          response.data.error || 'Website analysis failed. You can skip and continue.'
        );
      }
    } catch (error) {
      setIsAnalyzing(false);
      errorToast('Failed to analyze website. Please try again or skip.');
    }
  };

  // Update a text field in analysis data and sync to parent
  const updateAnalysisField = (field, value) => {
    const updated = { ...analysisData, [field]: value };
    setAnalysisData(updated);
    onUpdate({ websiteAnalysisData: updated });
  };

  // Remove a tag from an array field
  const removeTag = (field, index) => {
    const updated = {
      ...analysisData,
      [field]: analysisData[field].filter((_, i) => i !== index),
    };
    setAnalysisData(updated);
    onUpdate({ websiteAnalysisData: updated });
  };

  // Add a tag to an array field
  const addTag = (field, value) => {
    if (!value.trim()) return;
    const current = analysisData[field] || [];
    if (current.includes(value.trim())) return;
    const updated = {
      ...analysisData,
      [field]: [...current, value.trim()],
    };
    setAnalysisData(updated);
    onUpdate({ websiteAnalysisData: updated });
    setTagInputs((prev) => ({ ...prev, [field]: '' }));
  };

  const handleTagKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(field, tagInputs[field] || '');
    }
  };

  const handleContinue = () => {
    if (websiteUrl && !analysisComplete && !canSkip) {
      errorToast('Please analyze the website or clear the URL to skip');
      return;
    }

    const currentData = {
      websiteUrl: websiteUrl || null,
      websiteAnalysisData: analysisData,
    };

    onUpdate(currentData);

    // Pass data directly to save (avoids async state update issues)
    if (onSaveAndContinue) {
      onSaveAndContinue(currentData);
    } else {
      onNext();
    }
  };

  const handleSkip = () => {
    onUpdate({
      websiteUrl: null,
      websiteAnalysisData: null,
    });
    onNext();
  };

  // Blog Discovery Functions
  const handleDiscoverBlogs = async () => {
    if (!websiteUrl || !data.brandProfile?.id) {
      errorToast('Website URL and brand profile required');
      return;
    }

    setIsDiscoveringBlogs(true);
    setDiscoveredBlogs([]);
    setSelectedBlogs([]);
    setBlogImportResults(null);

    try {
      const response = await onBoardingService.discoverBlogs(
        data.brandProfile.id,
        websiteUrl,
        30
      );

      if (response.data.success && response.data.data?.blog_posts?.length > 0) {
        const blogs = response.data.data.blog_posts;
        setDiscoveredBlogs(blogs);
        setSelectedBlogs(blogs.map(b => b.url)); // Select all by default
        setShowBlogSection(true);
        successToast(`Found ${blogs.length} blog posts!`);
      } else {
        setShowBlogSection(true);
        setDiscoveredBlogs([]);
        errorToast('No blog posts found on this website');
      }
    } catch (error) {
      console.error('Blog discovery error:', error);
      errorToast('Failed to discover blog posts');
    } finally {
      setIsDiscoveringBlogs(false);
    }
  };

  const handleImportBlogs = async () => {
    if (selectedBlogs.length === 0) {
      errorToast('Please select at least one blog post to import');
      return;
    }

    setIsImportingBlogs(true);

    try {
      const response = await onBoardingService.importBlogs(
        data.brandProfile.id,
        selectedBlogs
      );

      if (response.data.success) {
        const results = response.data.data;
        setBlogImportResults(results);
        successToast(`Imported ${results.imported} blog posts for training!`);

        // Update parent data
        onUpdate({
          ...data,
          blogPostsImported: results.imported,
        });
      } else {
        errorToast(response.data.error || 'Failed to import blog posts');
      }
    } catch (error) {
      console.error('Blog import error:', error);
      errorToast('Failed to import blog posts');
    } finally {
      setIsImportingBlogs(false);
    }
  };

  const toggleBlogSelection = (url) => {
    setSelectedBlogs(prev =>
      prev.includes(url)
        ? prev.filter(u => u !== url)
        : [...prev, url]
    );
  };

  const selectAllBlogs = () => {
    setSelectedBlogs(discoveredBlogs.map(b => b.url));
  };

  const deselectAllBlogs = () => {
    setSelectedBlogs([]);
  };

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <h2 className={Styles.title}>Website Analysis</h2>
        <p className={Styles.subtitle}>
          Let our AI analyze your website to extract your brand positioning,
          voice, and key themes (Optional)
        </p>
      </div>

      {/* Form Content */}
      <div className={Styles.formContent}>
        {/* Website URL Input */}
        <div className={Styles.section}>
          <label htmlFor="websiteUrl" className={Styles.label}>
            Website URL
          </label>
          <div className={Styles.urlInputWrapper}>
            <div className={Styles.iconPrefix}>
              <FiLink />
            </div>
            <input
              id="websiteUrl"
              type="text"
              className={Styles.urlInput}
              placeholder="yourbrand.com or https://yourbrand.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              disabled={isAnalyzing || analysisComplete}
            />
            {!analysisComplete && !isAnalyzing && websiteUrl && (
              <button
                type="button"
                className={Styles.analyzeBtn}
                onClick={() => handleAnalyze()}
              >
                <FiRefreshCw />
                Analyze
              </button>
            )}
            {analysisComplete && (
              <div className={Styles.successBadge}>
                <FiCheck />
                Analyzed
              </div>
            )}
          </div>
          <p className={Styles.helpText}>
            We'll scan your website to understand your brand voice, messaging,
            and positioning
          </p>
        </div>

        {/* Analysis Status */}
        {isAnalyzing && (
          <div className={Styles.statusCard}>
            <div className={Styles.statusIcon}>
              <div className={Styles.spinner}></div>
            </div>
            <div className={Styles.statusContent}>
              <div className={Styles.statusTitle}>Analyzing your website...</div>
              <div className={Styles.statusSubtitle}>
                This typically takes 15-30 seconds. We're extracting your brand
                voice, key themes, and positioning.
              </div>
            </div>
          </div>
        )}

        {/* Analysis Results Preview */}
        {analysisComplete && analysisData && (
          <div className={Styles.resultsCard}>
            <div className={Styles.resultsHeader}>
              <FiCheck className={Styles.resultsIcon} />
              <span className={Styles.resultsTitle}>Analysis Complete</span>
              <span className={Styles.editHint}>Review and edit the results below</span>
            </div>
            <div className={Styles.resultsContent}>
              {(analysisData.brand_description != null) && (
                <div className={Styles.resultItem}>
                  <span className={Styles.resultLabel}>Description:</span>
                  <span
                    className={Styles.resultValue}
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => updateAnalysisField('brand_description', e.target.innerText)}
                  >
                    {analysisData.brand_description}
                  </span>
                </div>
              )}
              <div className={Styles.resultItem}>
                <span className={Styles.resultLabel}>Industries:</span>
                <div className={Styles.tagList}>
                  {(analysisData.industries || []).map((industry, i) => (
                    <span key={i} className={Styles.tag}>
                      {industry}
                      <button type="button" className={Styles.tagRemove} onClick={() => removeTag('industries', i)}><FiX size={12} /></button>
                    </span>
                  ))}
                  <input
                    className={Styles.tagInput}
                    value={tagInputs.industries || ''}
                    onChange={(e) => setTagInputs((prev) => ({ ...prev, industries: e.target.value }))}
                    onKeyDown={(e) => handleTagKeyDown(e, 'industries')}
                    placeholder="+ Add"
                  />
                </div>
              </div>
              {(analysisData.value_proposition != null) && (
                <div className={Styles.resultItem}>
                  <span className={Styles.resultLabel}>Value Proposition:</span>
                  <span
                    className={Styles.resultValue}
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => updateAnalysisField('value_proposition', e.target.innerText)}
                  >
                    {analysisData.value_proposition}
                  </span>
                </div>
              )}
              {(analysisData.core_messaging != null) && (
                <div className={Styles.resultItem}>
                  <span className={Styles.resultLabel}>Core Messaging:</span>
                  <span
                    className={Styles.resultValue}
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => updateAnalysisField('core_messaging', e.target.innerText)}
                  >
                    {analysisData.core_messaging}
                  </span>
                </div>
              )}
              <div className={Styles.resultItem}>
                <span className={Styles.resultLabel}>Tone:</span>
                <div className={Styles.tagList}>
                  {(analysisData.tone || []).map((tone, i) => (
                    <span key={i} className={Styles.tag}>
                      {tone}
                      <button type="button" className={Styles.tagRemove} onClick={() => removeTag('tone', i)}><FiX size={12} /></button>
                    </span>
                  ))}
                  <input
                    className={Styles.tagInput}
                    value={tagInputs.tone || ''}
                    onChange={(e) => setTagInputs((prev) => ({ ...prev, tone: e.target.value }))}
                    onKeyDown={(e) => handleTagKeyDown(e, 'tone')}
                    placeholder="+ Add"
                  />
                </div>
              </div>
              {analysisData.target_audience && analysisData.target_audience.length > 0 && (
                <div className={Styles.resultItem}>
                  <span className={Styles.resultLabel}>Target Audience:</span>
                  <div className={Styles.tagList}>
                    {analysisData.target_audience.map((audience, i) => (
                      <span key={i} className={Styles.tag}>
                        {audience}
                        <button type="button" className={Styles.tagRemove} onClick={() => removeTag('target_audience', i)}><FiX size={12} /></button>
                      </span>
                    ))}
                    <input
                      className={Styles.tagInput}
                      value={tagInputs.target_audience || ''}
                      onChange={(e) => setTagInputs((prev) => ({ ...prev, target_audience: e.target.value }))}
                      onKeyDown={(e) => handleTagKeyDown(e, 'target_audience')}
                      placeholder="+ Add"
                    />
                  </div>
                </div>
              )}
              {analysisData.keywords_and_themes && analysisData.keywords_and_themes.length > 0 && (
                <div className={Styles.resultItem}>
                  <span className={Styles.resultLabel}>Keywords & Themes:</span>
                  <div className={Styles.tagList}>
                    {analysisData.keywords_and_themes.map((keyword, i) => (
                      <span key={i} className={Styles.tag}>
                        {keyword}
                        <button type="button" className={Styles.tagRemove} onClick={() => removeTag('keywords_and_themes', i)}><FiX size={12} /></button>
                      </span>
                    ))}
                    <input
                      className={Styles.tagInput}
                      value={tagInputs.keywords_and_themes || ''}
                      onChange={(e) => setTagInputs((prev) => ({ ...prev, keywords_and_themes: e.target.value }))}
                      onKeyDown={(e) => handleTagKeyDown(e, 'keywords_and_themes')}
                      placeholder="+ Add"
                    />
                  </div>
                </div>
              )}
              {analysisData.detailed_topics && analysisData.detailed_topics.length > 0 && (
                <div className={Styles.resultItem}>
                  <span className={Styles.resultLabel}>Content Topics:</span>
                  <div className={Styles.tagList}>
                    {analysisData.detailed_topics.map((topic, i) => (
                      <span key={i} className={Styles.tag}>
                        {topic}
                        <button type="button" className={Styles.tagRemove} onClick={() => removeTag('detailed_topics', i)}><FiX size={12} /></button>
                      </span>
                    ))}
                    <input
                      className={Styles.tagInput}
                      value={tagInputs.detailed_topics || ''}
                      onChange={(e) => setTagInputs((prev) => ({ ...prev, detailed_topics: e.target.value }))}
                      onKeyDown={(e) => handleTagKeyDown(e, 'detailed_topics')}
                      placeholder="+ Add"
                    />
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              className={Styles.reanalyzeBtn}
              onClick={() => {
                setAnalysisComplete(false);
                setAnalysisData(null);
                // Trigger re-scrape directly with force_refresh to bypass all caches
                setTimeout(() => handleAnalyze(true), 0);
              }}
            >
              <FiRefreshCw />
              Re-analyze
            </button>
          </div>
        )}

        {/* Blog Discovery Section - Shows after analysis complete */}
        {analysisComplete && data.brandProfile?.id && (
          <div className={Styles.blogSection}>
            <div className={Styles.blogSectionHeader}>
              <div className={Styles.blogSectionTitle}>
                <FiFileText className={Styles.blogIcon} />
                <span>Import Blog Content for Training</span>
              </div>
              {!showBlogSection && !isDiscoveringBlogs && (
                <button
                  type="button"
                  className={Styles.discoverBtn}
                  onClick={handleDiscoverBlogs}
                >
                  <FiSearch />
                  Discover Blog Posts
                </button>
              )}
            </div>

            {isDiscoveringBlogs && (
              <div className={Styles.blogLoading}>
                <div className={Styles.spinner}></div>
                <span>Searching for blog posts...</span>
              </div>
            )}

            {showBlogSection && !isDiscoveringBlogs && (
              <div className={Styles.blogContent}>
                {discoveredBlogs.length > 0 ? (
                  <>
                    <div className={Styles.blogListHeader}>
                      <span className={Styles.blogCount}>
                        Found {discoveredBlogs.length} blog posts
                      </span>
                      <div className={Styles.blogActions}>
                        <button
                          type="button"
                          className={Styles.selectAllBtn}
                          onClick={selectAllBlogs}
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          className={Styles.deselectAllBtn}
                          onClick={deselectAllBlogs}
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>

                    <div className={Styles.blogList}>
                      {discoveredBlogs.slice(0, 15).map((blog, idx) => (
                        <div
                          key={idx}
                          className={`${Styles.blogItem} ${
                            selectedBlogs.includes(blog.url) ? Styles.selected : ''
                          }`}
                          onClick={() => toggleBlogSelection(blog.url)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedBlogs.includes(blog.url)}
                            onChange={() => toggleBlogSelection(blog.url)}
                            className={Styles.blogCheckbox}
                          />
                          <div className={Styles.blogInfo}>
                            <span className={Styles.blogTitle}>
                              {blog.title || blog.url.split('/').pop() || 'Blog Post'}
                            </span>
                            <span className={Styles.blogUrl}>{blog.url}</span>
                          </div>
                          <a
                            href={blog.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={Styles.blogExternalLink}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FiExternalLink />
                          </a>
                        </div>
                      ))}
                      {discoveredBlogs.length > 15 && (
                        <div className={Styles.blogMoreNotice}>
                          + {discoveredBlogs.length - 15} more posts (first 15 shown)
                        </div>
                      )}
                    </div>

                    <div className={Styles.blogImportActions}>
                      <button
                        type="button"
                        className={Styles.importBtn}
                        onClick={handleImportBlogs}
                        disabled={isImportingBlogs || selectedBlogs.length === 0}
                      >
                        {isImportingBlogs ? (
                          <>
                            <div className={Styles.spinnerSmall}></div>
                            Importing...
                          </>
                        ) : (
                          <>
                            <FiDownload />
                            Import {selectedBlogs.length} Posts
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className={Styles.cancelBlogBtn}
                        onClick={() => {
                          setShowBlogSection(false);
                          setDiscoveredBlogs([]);
                          setSelectedBlogs([]);
                        }}
                      >
                        <FiX />
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <div className={Styles.noBlogsFound}>
                    <FiFileText className={Styles.noBlogsIcon} />
                    <span>No blog posts found on this website.</span>
                    <button
                      type="button"
                      className={Styles.retryDiscoverBtn}
                      onClick={handleDiscoverBlogs}
                    >
                      <FiRefreshCw />
                      Try Again
                    </button>
                  </div>
                )}

                {blogImportResults && (
                  <div className={Styles.importResults}>
                    <FiCheck className={Styles.importSuccessIcon} />
                    <span>
                      Successfully imported {blogImportResults.imported} blog posts!
                      {blogImportResults.skipped > 0 && (
                        <> ({blogImportResults.skipped} already imported)</>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}

            {!showBlogSection && !isDiscoveringBlogs && (
              <p className={Styles.blogHelpText}>
                Import your blog content to help train our AI on your writing style
              </p>
            )}
          </div>
        )}

        {/* Info Box */}
        <div className={Styles.infoBox}>
          <FiAlertCircle className={Styles.infoIcon} />
          <div className={Styles.infoContent}>
            <div className={Styles.infoTitle}>Why analyze your website?</div>
            <div className={Styles.infoText}>
              Our AI will extract your existing brand positioning, voice
              characteristics, and key themes to pre-populate future steps,
              saving you time while ensuring consistency.
            </div>
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className={Styles.footer}>
        <button
          type="button"
          className={Styles.previousBtn}
          onClick={onPrevious}
        >
          Previous
        </button>
        <button type="button" className={Styles.saveExitBtn} onClick={onSaveAndExit}>
          Save & Exit
        </button>
        <div className={Styles.stepIndicator}>Step 3 of 7</div>
        <div className={Styles.footerActions}>
          {!analysisComplete && (
            <button
              type="button"
              className={Styles.skipBtn}
              onClick={handleSkip}
            >
              Skip
            </button>
          )}
          <BSButton
            classList={Styles.continueBtn}
            onClick={handleContinue}
            disabled={isAnalyzing}
          >
            Save & Continue
          </BSButton>
        </div>
      </div>
    </div>
  );
};

export default Step2WebsiteAnalysis;
